import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useActivityActions } from "../contexts/ActivityContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  UsersIcon,
  MapPinIcon,
  MessageCircleIcon,
  BellIcon,
  CameraIcon,
  SearchIcon,
  CalendarIcon,
  HeartIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";

import { crashlytics } from "../services/firebase";
interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  ctaText: string;
  backgroundColor: string;
  iconColor: string;
}

const OnboardingPage: React.FC = () => {
  usePageTracking("Onboarding", "OnboardingPage");
  const navigate = useNavigate();
  const { markOnboardingComplete } = useAuth();
  const { createUserJoinedAuditLog, fetchRecommendations, prefetchIdeas } =
    useActivityActions();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);

  // Preload ForYou page components and data in the background
  useEffect(() => {
    // Dynamically import ForYou page to start loading it
    const preloadForYouPage = async () => {
      try {
        await import("../pages/ForYouPage");
      } catch (error) {
        crashlytics.logError(
          `Error preloading ForYou page: ${JSON.stringify(error?.message)}`,
          "OnboardingPage.tsx",
          error,
        );
      }
    };

    // Start fetching recommendations in the background
    const prefetchData = async () => {
      try {
        await fetchRecommendations();
        // Also prefetch ideas for CreatePage
        await prefetchIdeas();
      } catch (error) {
        crashlytics.logError(
          `Error prefetching recommendations and ideas: ${JSON.stringify(error?.message)}`,
          "OnboardingPage.tsx",
          error,
        );
      }
    };

    preloadForYouPage();

    // Start prefetching data after a short delay to not block the onboarding UI
    const dataTimeout = setTimeout(() => {
      prefetchData();
    }, 1000);

    return () => clearTimeout(dataTimeout);
  }, [fetchRecommendations, prefetchIdeas]);

  const onboardingSteps: OnboardingStep[] = [
    {
      id: "welcome",
      title: "Welcome to Who's in?!",
      description:
        "Connect with people who share your interests and discover amazing activities together.",
      icon: <HeartIcon className="w-12 h-12" />,
      features: [
        "Find people with similar interests",
        "Discover new activities daily",
        "Build meaningful connections",
        "Explore your city together",
      ],
      ctaText: "Get Started",
      backgroundColor: "bg-gradient-to-br from-blue-500 to-purple-600",
      iconColor: "text-white",
    },
    {
      id: "discover",
      title: "Discover Activities",
      description:
        "Browse through curated activities or create your own. From coffee meetups to hiking adventures!",
      icon: <SearchIcon className="w-12 h-12" />,
      features: [
        'Browse "For You" personalized feed',
        "Search by category or location",
        "Filter by date, time, and distance",
        "See who's already joined",
      ],
      ctaText: "Continue",
      backgroundColor: "bg-gradient-to-br from-green-500 to-blue-500",
      iconColor: "text-white",
    },
    {
      id: "create",
      title: "Create Your Own",
      description:
        "Have an idea? Create activities and invite others to join your adventures.",
      icon: <CalendarIcon className="w-12 h-12" />,
      features: [
        "AI-powered activity creation",
        "Set location and time easily",
        "Choose participant limits",
        "Manage your events effortlessly",
      ],
      ctaText: "Continue",
      backgroundColor: "bg-gradient-to-br from-orange-500 to-red-500",
      iconColor: "text-white",
    },
    {
      id: "connect",
      title: "Connect & Chat",
      description:
        "Message other participants, build friendships, and coordinate your meetups.",
      icon: <MessageCircleIcon className="w-12 h-12" />,
      features: [
        "Group chats for each activity",
        "Direct messages with connections",
        "Real-time notifications",
        "Share photos and updates",
      ],
      ctaText: "Continue",
      backgroundColor: "bg-gradient-to-br from-pink-500 to-purple-500",
      iconColor: "text-white",
    },
    {
      id: "profile",
      title: "Build Your Profile",
      description:
        "Let others know who you are! Add photos, interests, and tell your story.",
      icon: <CameraIcon className="w-12 h-12" />,
      features: [
        "Upload profile photos easily",
        "Share your interests and passions",
        "Add your location preferences",
        "Connect social accounts (optional)",
      ],
      ctaText: "Continue",
      backgroundColor: "bg-gradient-to-br from-indigo-500 to-blue-600",
      iconColor: "text-white",
    },
    {
      id: "ready",
      title: "You're All Set!",
      description:
        "Ready to start your Who's in? journey? Let's find your first activity!",
      icon: <CheckIcon className="w-12 h-12" />,
      features: [
        "Your personalized feed awaits",
        "Start browsing activities now",
        "Join your first meetup",
        "Make new connections today",
      ],
      ctaText: "Complete Setup",
      backgroundColor: "bg-gradient-to-br from-emerald-500 to-green-600",
      iconColor: "text-white",
    },
  ];

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Mark onboarding as complete and create audit log
      await markOnboardingComplete();
      await createUserJoinedAuditLog();
      navigate("/foryou", { replace: true });
    } catch (error) {
      crashlytics.logError(
        `Error completing onboarding: ${JSON.stringify(error?.message)}`,
        "OnboardingPage.tsx",
        error,
      );
    } finally {
      setIsCompleting(false);
    }
  };

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <div className="h-full-safe flex flex-col bg-white overflow-hidden">
      {/* Progress Bar */}
      <div
        className="w-full bg-gray-200 h-1 fixed z-50"
        style={{ top: "calc(env(safe-area-inset-top) + 0.5rem)" }}
      >
        <div
          className="bg-primary h-1 transition-all duration-500 ease-out"
          style={{
            width: `${((currentStep + 1) / onboardingSteps.length) * 100}%`,
          }}
        />
      </div>

      {/* Header */}
      <div
        className="flex justify-between items-center p-4 flex-shrink-0"
        style={{ paddingTop: "3rem" }}
      >
        <div className="flex items-center space-x-2">
          {onboardingSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index <= currentStep ? "bg-primary" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
        <div className="w-12"></div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-6 pt-4 overflow-y-auto hide-scrollbar">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="w-full flex-1 flex flex-col max-w-md text-center"
          >
            {/* Icon and Title */}
            <div className="mb-8">
              <div
                className={`w-24 h-24 mx-auto mb-6 rounded-full ${currentStepData.backgroundColor} flex items-center justify-center shadow-lg`}
              >
                <div className={currentStepData.iconColor}>
                  {currentStepData.icon}
                </div>
              </div>

              <h1 className="text-3xl font-bold text-gray-900">
                {currentStepData.title}
              </h1>
            </div>

            {/* Description */}
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {currentStepData.description}
            </p>

            {/* Features */}
            <div className="space-y-5">
              {currentStepData.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                  className="flex items-center text-left bg-gray-50 rounded-lg p-5"
                >
                  <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <CheckIcon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-lg text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="px-4 pt-4 pb-safe-or-4 space-y-3 flex-shrink-0">
        <button
          onClick={handleNext}
          disabled={isCompleting}
          className="w-full bg-primary text-white py-3.5 rounded-xl font-semibold text-base flex items-center justify-center space-x-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isCompleting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Setting up...</span>
            </>
          ) : (
            <>
              <span>{currentStepData.ctaText}</span>
              <ArrowRightIcon className="w-5 h-5" />
            </>
          )}
        </button>

        {currentStep > 0 && (
          <button
            onClick={handlePrevious}
            className="w-full text-gray-500 py-2.5 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Previous</span>
          </button>
        )}
      </div>

      {/* Step Counter */}
      <div className="text-center pb-4 flex-shrink-0">
        <span className="text-xs text-gray-400">
          {currentStep + 1} of {onboardingSteps.length}
        </span>
      </div>
    </div>
  );
};

export default OnboardingPage;
